import { useEffect, useRef, useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import { Menu } from '~/modules/elements/akui/menu';
import Modal from '~/modules/elements/modal';
import { NavButton } from '~/modules/elements/nav-controls';
import GameState from '~/modules/game-engine/game-state/game-state';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import SongsService from '~/modules/songs/songs-service';
import RateSong from '~/routes/game/singing/game-overlay/components/rate-song';
import { formatSongPosition } from '~/routes/game/singing/game-overlay/helpers/song-position';
import SelectInputModal from '~/routes/select-input/select-input-modal';
import { InGameInputLag } from '~/routes/settings/in-game-audio-settings';
import { InputLagSetting, useSettingValue } from '~/routes/settings/settings-state';

interface Props {
  onResume: () => void;
  onExit?: () => void;
  onRestart: () => void;
  open: boolean;
  // Set when the game stopped itself rather than being paused by the room - see `player.tsx`.
  reason?: string | null;
}

// The menu is the only thing on screen saying what you came back to: the song behind it is blurred to
// 20px by the modal backdrop, so the title and position are read off the paused clock, once.
const PausedSong = () => {
  const [song] = useState(() => GameState.getSong());
  const [position] = useState(() => formatSongPosition(GameState.getCurrentTime(false), GameState.getDuration()));

  if (!song) return null;

  return (
    <div className="landscap:-mt-1 -mt-2 flex items-baseline justify-center gap-2.5">
      <span className="tv:text-[1.375rem] text-[1.125rem] font-bold text-white/80">{song.title}</span>
      <span className="tv:text-[1.125rem] text-[0.9375rem] font-normal text-white/40">
        {[song.artist, position].filter(Boolean).join(' · ')}
      </span>
    </div>
  );
};

const PauseMenuContent = ({ onResume, onExit, onRestart, reason }: Omit<Props, 'open'>) => {
  const navigate = useSmoothNavigate();
  const menuRef = useRef<null | HTMLButtonElement>(null);

  useEffect(() => {
    menuRef.current?.focus();
  }, [menuRef]);

  const [isInputModalOpen, setIsInputModalOpen] = useState(false);
  const [inputLag] = useSettingValue(InputLagSetting);

  const [rateSongOpen, setRateSongOpen] = useState(false);

  const { register } = useKeyboardNav({
    enabled: !isInputModalOpen,
    // Shared hook: the pause list and the rate-song view render into it one at a time, so the header
    // tracks whichever is showing.
    title: rateSongOpen ? 'Rate the song' : 'Pause menu',
    // One hook, two screens - so the action named is whichever screen is showing. Restart, Exit and
    // Microphones settings are all things you might do instead of resuming, not lesser versions of it.
    primary: rateSongOpen ? 'button-song-ok' : 'button-resume-song',
    titleIcon: rateSongOpen ? undefined : 'pause',
  });

  const [isUnverifiedSongFlow, setIsUnverifiedSongFlow] = useState(false);
  const handleExit = async () => {
    const progress = GameState.getSongCompletionProgress();
    const currentSong = GameState.getSong();
    const songPreview = (await SongsService.getIndex()).find((song) => song.id === currentSong?.id);
    const shouldForceSharedRating = !!currentSong?.isUnverifiedSong && !songPreview;

    if (shouldForceSharedRating) {
      setIsUnverifiedSongFlow(true);
      setRateSongOpen(true);
      return;
    }

    if (!songPreview?.local && progress < 0.7) {
      setIsUnverifiedSongFlow(false);
      setRateSongOpen(true);
    } else {
      onExit?.();
    }
  };

  return (
    <>
      {!rateSongOpen && (
        <KeyboardNavContext value={register}>
          <Menu
            modal
            className="landscap:gap-2 landscap:p-4 2xl:max-w-[45rem]"
            title={
              // F7's sizes need the 1920x1080 it was drawn at: the six entries alone are 599px, so at
              // 720p the full-size header is what tips the card past the screen.
              <span className="stroke-text landscap:text-lg tv:text-2xl flex items-center gap-3 text-xl">
                <Icon icon="ic:baseline-pause" width={30} height={30} />
                Paused
              </span>
            }>
            {/* The warning the slots carry in mic check, in the one place the room is looking. Same
                red as `PlayerStatus`, so a disconnected device reads the same wherever it is said. */}
            {reason && (
              <div
                data-test="pause-reason"
                className="landscap:text-sm mx-auto rounded-xl bg-red-950/95 px-3 py-2 text-center text-base text-red-100">
                {reason}
              </div>
            )}
            <PausedSong />
            {/* Resume starts playback again, so it reads as "play" rather than a generic back arrow. */}
            <NavButton
              name="button-resume-song"
              remoteIcon="play"
              onClick={onResume}
              ref={menuRef}
              className="landscap:h-[3.125rem]">
              Resume song
            </NavButton>
            <NavButton
              name="button-restart-song"
              remoteIcon={null}
              onClick={onRestart}
              className="landscap:h-[3.125rem]">
              Restart song
            </NavButton>
            <NavButton name="button-exit-song" remoteIcon={null} onClick={handleExit} className="landscap:h-[3.125rem]">
              Exit song
            </NavButton>
            <NavButton
              name="input-settings"
              remoteIcon="settings"
              onClick={() => setIsInputModalOpen(true)}
              className="landscap:h-[3.125rem]">
              Microphones settings
            </NavButton>
            <Menu.Divider className="my-1" />
            <InGameInputLag value={inputLag} />
            {/* Hidden from the phone: it opens the full song editor, which is a desktop/TV-only
                surface the remote can't drive. */}
            <NavButton
              name="edit-song"
              hideOnRemote
              size="small"
              className="text-md"
              onClick={() => navigate(`edit/song/`, { song: GameState.getSong()?.id ?? '' })}>
              Edit song
            </NavButton>
          </Menu>
          <SelectInputModal onClose={() => setIsInputModalOpen(false)} open={isInputModalOpen} />
        </KeyboardNavContext>
      )}
      {rateSongOpen && (
        <RateSong
          onExit={onExit}
          onBack={() => setRateSongOpen(false)}
          register={register}
          song={GameState.getSong()}
          isUnverifiedSong={isUnverifiedSongFlow}
        />
      )}
    </>
  );
};

export default function PauseMenu({ onResume, onExit, onRestart, open, reason }: Props) {
  return (
    <Modal onClose={onResume} open={open}>
      {open && <PauseMenuContent onResume={onResume} onExit={onExit} onRestart={onRestart} reason={reason} />}
    </Modal>
  );
}
